import React, { useContext, useEffect, useState } from "react";
import { commitMutation, graphql, QueryRenderer } from "react-relay";
import type { RouteComponentProps } from "react-router";

import { faArchive, faHandPointRight } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faUserFriends } from "@awesome.me/kit-4c83d50d0d/icons/kit/custom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Sentry from "@sentry/browser";
import cx from "classnames";
import JSZip from "jszip";
import moment from "moment";
import Papa from "papaparse";

import { NoteAndFeedbackHistoryPaywall } from "~/billing";
import { HeaderAction } from "~/common/PageHeader";
import ThemeContext from "~/common/ThemeContext";
import {
	anonymizeFeedbackUser,
	questionAnswerToText,
	replaceLinks,
	ThreeSixtyRelation,
	ThreeSixtyRelationPluralReverse,
} from "~/feedback/utils";
import { type MinimalTeamsContext, TeamsContext } from "~/integrations/teams/teamsContext";
import { OuterObjectContext } from "~/lib/outerObjectContext";
import { download, inCalendarMode, inVideocallMode, isZoom } from "~/lib/utils";
import { environment } from "~/models";
import { Button } from "~/ui";
import ArrowDropdown, { DropdownItem } from "~/ui/ArrowDropdown";
import Avatar from "~/ui/legacy/Avatar";
import type { UserAttrs } from "~/ui/legacy/Avatar";
import { ComplexTabs, SimpleTabs } from "~/ui/legacyFellowIndex";
import toast from "~/ui/toast";

import FeedbackContext from "../FeedbackContext";
import { FeedbackCard, MarkAsComplete, MarkAsPriority } from "../FeedbackList/FeedbackCard";
import ListHeader from "../FeedbackList/ListHeader";
import { FeedbackSubject } from "../FeedbackSubject";
import GaveFeedbackModal from "../GaveFeedbackModal";
import ItemPageHeader from "../ItemPageHeader";
import NotFound from "../NotFound";
import NudgeRequestees from "../NudgeRequestees";
import { type Question as QuestionType, validateQuestions } from "../ResponseForm/validation";
import { ReuseFeedbackQuestions } from "../ReuseQuestions";
import { ReactComponent as IlloDark } from "./css/illo-dark.svg";
import { ReactComponent as Illo } from "./css/illo.svg";
import MoreActions, { shareFeedbackWith } from "./MoreActions";
import Person, { renderUser } from "./Person";
import Question, { type Answer } from "./Question";

import type { FeedbackCardMarkAsCompleteMutation } from "~/__generated__/FeedbackCardMarkAsCompleteMutation.graphql";
import type { FeedbackCardMarkAsPriorityMutation } from "~/__generated__/FeedbackCardMarkAsPriorityMutation.graphql";
import type {
	FeedbackItemQuery as FeedbackItemQueryType,
	FeedbackItemQuery$data as QueryResponse,
} from "~/__generated__/FeedbackItemQuery.graphql";
import type { FeedbackItemRecipientSawFeedbackMutation as SawFeedbackType } from "~/__generated__/FeedbackItemRecipientSawFeedbackMutation.graphql";

import stylesFeedbackRequestItem from "~/feedback/css/FeedbackRequestItem.module.css";
import stylesMoreActions from "~/feedback/FeedbackItem/css/MoreActions.module.css";

import styles from "./css/FeedbackItem.module.css";

export const RecipientSawFeedback = graphql`
	mutation FeedbackItemRecipientSawFeedbackMutation($id: ID!) {
		recipientSawFeedback(id: $id) {
			feedback {
				lastSeenByUser
			}
		}
	}
`;

export function markAsRead(id: string) {
	commitMutation<SawFeedbackType>(environment, {
		mutation: RecipientSawFeedback,
		variables: { id },
		updater: store => {
			const viewer = store.get("viewer");

			if (!viewer) return;

			const count = viewer.getValue("feedbackUnreadItemsCount");
			const newValue = typeof count == "number" && count > 0 ? count - 1 : 0;

			viewer.setValue(newValue, "feedbackUnreadItemsCount");
		},
	});
}

type RendererProps = RouteComponentProps<{ id?: string }, {}, { requestedFeedbackId?: string } | undefined | null> & {
	goBack?: () => void;
	isChild?: boolean;
	threeSixtyParentTab?: keyof typeof ThreeSixtyRelation;
	registerCSVExportMethod?: (method: Promise<CSVExportMethodType>) => void;
};
type Props = Omit<RendererProps, "registerCSVExportMethod"> &
	QueryResponse & {
		registerCSVExportMethod?: { wrappedSetRegisterCSVExportMethod: (method: CSVExportMethodType) => void };
	} & typeof FeedbackItem.defaultProps & {
		outerObjectContext: React.ContextType<typeof OuterObjectContext>;
	} & { teamsContext: MinimalTeamsContext | undefined };
type Request = NonNullable<NonNullable<QueryResponse["viewer"]["feedback"]>["requests"]>[0];

const USER_FILTER_DEFAULT_VALUE = "All Respondents";
const TABS = ["By Question", "By Person"] as const;
type TabType = (typeof TABS)[number];

interface PersonFilterItem {
	text: string;
	user: UserAttrs;
	onClick: (e: React.MouseEvent) => void;
	repliedAt?: string | null;
}

const THREE_SIXTY_TABS = Object.keys(ThreeSixtyRelation) as Array<keyof typeof ThreeSixtyRelation>;
type ThreeSixtyTabKeyType = (typeof THREE_SIXTY_TABS)[number];

interface State {
	tab: TabType;
	requestee: string;
	nudgeOpen: boolean;
	nudgeSelected: boolean | string[];
	reuseQuestionsOpen: boolean;
	previousLastSeenByUser: string;
	threeSixtyTab: ThreeSixtyTabKeyType;
}

const isValidTab = (tab: string): tab is TabType => TABS.includes(tab as TabType);

const NewBadge = () => <span className={styles.newBadge}>New</span>;

function Tab({ name, badge, active }: { name: string; badge: string; active: boolean }) {
	return (
		<div className={styles.threeSixtyRelationTab}>
			<span className={cx(styles.tabLabel, { [styles.tabActive]: active })}>{name}</span>
			<span className={styles.threeSixtyRelationResponseCountBadge}>{badge}</span>
		</div>
	);
}

type TabDataType = {
	key: ThreeSixtyTabKeyType;
	name: string;
	relation: (typeof ThreeSixtyRelation)[keyof typeof ThreeSixtyRelation];
	label: (active: boolean) => React.ReactElement;
};

type CSVExportMethodType = () => { filename: string; content: string } | null;

const calculateTabs = (children: NonNullable<QueryResponse["viewer"]["feedback"]>["children"]): TabDataType[] => {
	const calculateTab = (
		key: ThreeSixtyTabKeyType,
		name: string,
		relation: (typeof ThreeSixtyRelation)[keyof typeof ThreeSixtyRelation],
	): TabDataType & { requestCount: number } => {
		const child = children ? children.find(child => child.threeSixtyRelation === relation) : undefined;

		return {
			key: key,
			name: name,
			requestCount: child ? child.requestCount : 0,
			label: function WrappedTab(active: boolean) {
				return <Tab name={name} active={active} badge={child ? `${child.responseCount}/${child.requestCount}` : ""} />;
			},
			relation: relation,
		};
	};

	return [
		calculateTab("self", "Self", ThreeSixtyRelation.self),
		calculateTab("manager", "Manager", ThreeSixtyRelation.manager),
		calculateTab("peers", "Peers", ThreeSixtyRelation.peers),
		calculateTab("directReports", "Direct Reports", ThreeSixtyRelation.directReports),
	].filter(tab => tab.requestCount > 0);
};

class FeedbackItem extends React.Component<Props, State> {
	state: State = {
		tab: "By Question",
		requestee: USER_FILTER_DEFAULT_VALUE,
		nudgeOpen: false,
		nudgeSelected: false,
		reuseQuestionsOpen: false,
		previousLastSeenByUser: "",
		threeSixtyTab: THREE_SIXTY_TABS[0],
	};

	static defaultProps = {
		isChild: false,
	};

	static contextType = FeedbackContext;
	declare context: React.ContextType<typeof FeedbackContext>;

	private childCSVExportMethods: Promise<CSVExportMethodType>[] = [];

	getCurrentThreeSixtyTab(tabs: TabDataType[]) {
		const existingTab = tabs.find(tab => tab.key === this.state.threeSixtyTab);
		if (existingTab) {
			return existingTab;
		}
		return tabs[0];
	}

	componentDidMount() {
		const {
			location,
			outerObjectContext,
			viewer: { feedback },
		} = this.props;

		if (location.hash) {
			const [tab, requestee] = location.hash.replace("#", "").split("/");
			if (isValidTab(tab)) this.setState({ tab, requestee });
		}

		if (feedback && feedback.id) {
			if (feedback.lastSeenByUser) this.setState({ previousLastSeenByUser: feedback.lastSeenByUser });

			markAsRead(feedback.id);

			outerObjectContext.setClearId(feedback.id);

			const { wrappedSetRegisterCSVExportMethod } = this.props?.registerCSVExportMethod || {};

			wrappedSetRegisterCSVExportMethod?.(this.createCSVData);
		}
	}

	componentWillUnmount() {
		const { outerObjectContext } = this.props;

		if (this.props.viewer.feedback?.id) {
			outerObjectContext.unsetClearId(this.props.viewer.feedback.id);
		}
	}

	markAsComplete = () => {
		const {
			viewer: { feedback },
			goBack,
			history,
		} = this.props;

		if (!feedback || !feedback.id) return;

		commitMutation<FeedbackCardMarkAsCompleteMutation>(environment, {
			mutation: MarkAsComplete,
			variables: {
				id: feedback.id,
			},
			onCompleted: (_data, err) => {
				if (err) {
					toast.error(`Could not mark this feedback as archived: ${err[0].message}`);
				} else {
					toast.success("Feedback Archived");

					if (goBack) {
						goBack();
					} else {
						history.goBack();
					}
				}
			},
		});
	};

	markAsPriority = () => {
		const {
			viewer: { feedback },
		} = this.props;

		if (!feedback || !feedback.id) return;

		commitMutation<FeedbackCardMarkAsPriorityMutation>(environment, {
			mutation: MarkAsPriority,
			variables: {
				id: feedback.id,
			},
			onCompleted: (_data, err) => {
				if (err) toast.error(`Could not mark as this feedback as unarchived: ${err[0].message}`);
			},
		});
	};

	editInFeedback = () => {
		const {
			history,
			viewer: { feedback },
		} = this.props;

		if (feedback && feedback.isThreeSixtyParent) {
			history.push(`/feedback/three-sixty/${feedback.id}/edit/`);
		} else if (feedback) {
			history.push(`/feedback/${feedback.id}/edit/`);
		}
	};

	shareWithManager = () => {
		const {
			viewer: { feedback, user },
		} = this.props;

		const manager = user.internalAttributes?.manager;

		if (!manager || !manager.id || !feedback || !feedback.id) return;

		shareFeedbackWith(feedback.id, manager);
	};

	renderHeader = () => {
		const {
			viewer: { feedback, user },
			goBack,
		} = this.props;

		if (!feedback) return;

		const { creator, recipients, sharedWithMe, isThreeSixtyParent } = feedback;
		const manager = user.internalAttributes?.manager;

		const sharableWithManager =
			!sharedWithMe && !!manager && (!recipients || !recipients.some(({ user }) => user.id === manager.id));
		const isUnsolicitedGiven =
			feedback.type === "UNSOLICITED" &&
			creator &&
			user.id === creator.id &&
			(!recipients || !recipients.some(({ user }) => user.id === creator.id));
		const subject = <FeedbackSubject feedback={feedback} user={user} />;
		const actions = [
			sharableWithManager && feedback.type === "UNSOLICITED" && !!creator && user.id !== creator.id && (
				<HeaderAction key="share-with-manager" className={styles.headerAction} onClick={this.shareWithManager}>
					Share with Manager
				</HeaderAction>
			),
			!isUnsolicitedGiven && (
				<HeaderAction
					key="mark-as-complete"
					className={styles.markAsCompleteButton}
					onClick={feedback.isArchived ? this.markAsPriority : this.markAsComplete}
				>
					<FontAwesomeIcon icon={faArchive} className={styles.dropdownItemIcon} />
					{feedback.isArchived ? "Unarchive" : "Archive"}
				</HeaderAction>
			),
		];

		const isEmbedded = isZoom || inVideocallMode || inCalendarMode || !!this.props.teamsContext;

		return (
			<ItemPageHeader
				subject={subject}
				actions={actions}
				mobileActions={
					isUnsolicitedGiven
						? []
						: [
								<DropdownItem
									key="mark-as-complete"
									icon={<DropdownItem.Icon icon={faArchive} />}
									onClick={feedback.isArchived ? this.markAsPriority : this.markAsComplete}
								>
									{feedback.isArchived ? "Unarchive" : "Archive"}
								</DropdownItem>,
							]
				}
				moreActions={
					isEmbedded || feedback.type === "UNSOLICITED"
						? []
						: [
								<MoreActions
									key="more-actions"
									feedback={feedback}
									viewer={this.props.viewer}
									nudgeUsers={this.nudgeUsers}
									onReuseQuestions={this.onReuseQuestions}
									showReuseQuestions={!isThreeSixtyParent}
									onExportCSV={this.onExportCSV}
								/>,
							]
				}
				goBack={goBack}
			/>
		);
	};

	onNudgeClose = () => this.setState({ nudgeOpen: false });

	onReuseQuestions = () => this.setState({ reuseQuestionsOpen: true });

	onReuseQuestionsClose = () => this.setState({ reuseQuestionsOpen: false });

	onReuseQuestionsComplete = (feedbackId: string) => {
		const { history } = this.props;

		this.onReuseQuestionsClose();

		history.push(`/feedback/${feedbackId}/draft/`);
	};

	onSetTab = (tab: TabType) => {
		if (tab === "By Question") {
			this.setState({ tab, requestee: USER_FILTER_DEFAULT_VALUE });
		} else if (tab === "By Person") {
			this.setState({ tab });
		}
	};

	filterByPerson = (requestee: string) => {
		/* requestee value is user.id */
		this.setState({ requestee });
	};

	resetByPersonFilter = () => {
		this.filterByPerson(USER_FILTER_DEFAULT_VALUE);
	};

	nudgeUsers = (e: React.MouseEvent, selected?: string) => {
		const nudgeSelected = selected ? [selected] : true;
		this.setState({ nudgeOpen: true, nudgeSelected });
	};

	getPersonFilterItems = (answered: boolean): PersonFilterItem[] => {
		const {
			viewer: { feedback, user },
		} = this.props;
		if (!feedback || !feedback.requests) {
			return [];
		}

		const users = feedback.requests
			.filter(request => (answered ? request.feedbackRecord : !request.feedbackRecord))
			.map(request => {
				if (feedback?.isAnonymous && request.user.id === user.id) {
					return { ...request, user: anonymizeFeedbackUser(feedback.id || "", request.user) };
				}
				return request;
			})
			.map(request => ({
				text: request.user.fullName,
				user: request.user,
				onClick: (e: React.MouseEvent) => {
					if (feedback.isAnonymous && !answered) return;
					return answered ? this.filterByPerson(request.user.id) : this.nudgeUsers(e, request.id);
				},
				repliedAt: request.feedbackRecord?.createdAt,
			}))
			.sort(({ text: textA }, { text: textB }) => (textA > textB ? 1 : -1));

		return users;
	};

	renderTabs = () => {
		const { viewer } = this.props;
		const { feedback } = viewer;
		const { tab, requestee, previousLastSeenByUser } = this.state;
		const nudgableUsers = this.getPersonFilterItems(false);
		const filterableUsers = this.getPersonFilterItems(true);
		let dropdownText = USER_FILTER_DEFAULT_VALUE;

		if (requestee !== USER_FILTER_DEFAULT_VALUE) {
			const user = filterableUsers.find(({ user }) => user.id === requestee);
			if (user) dropdownText = user.text;
		}

		return (
			<div className={styles.tabsContainer}>
				<SimpleTabs tabs={TABS} tab={tab} setTab={this.onSetTab} className={styles.tabs} />
				{tab === "By Person" && (
					<ArrowDropdown heading={tab} text={dropdownText} popupClassName={styles.dropdownPopup}>
						<DropdownItem
							icon={
								<div className={styles.dropdownItemIconContainer}>
									<FontAwesomeIcon icon={faUserFriends} className={styles.dropdownItemIcon} />
								</div>
							}
							onClick={this.resetByPersonFilter}
						>
							{USER_FILTER_DEFAULT_VALUE}
						</DropdownItem>
						{filterableUsers.map(({ text, user, onClick, repliedAt }) => {
							const isNew = previousLastSeenByUser && repliedAt && moment(previousLastSeenByUser).isBefore(repliedAt);
							return (
								<DropdownItem
									key={user.id}
									onClick={onClick}
									icon={<Avatar user={user} link={false} className={styles.avatar} />}
									selected={requestee === user.id}
									className={cx(styles.dropdownItem, styles.dropdownItemWithBadge, {
										[styles.selected]: requestee === user.id,
									})}
								>
									<span className={styles.dropdownItemText}>{text}</span>
									{isNew && <NewBadge />}
								</DropdownItem>
							);
						})}
						{nudgableUsers.length > 0 && (
							<hr className={cx(styles.dropdownDivider, stylesMoreActions.dropdownDivider)} />
						)}
						{nudgableUsers.map(({ text, user, onClick }) => (
							<DropdownItem
								key={user.id}
								onClick={onClick}
								icon={<Avatar user={user} link={false} className={styles.avatar} />}
								className={cx(styles.dropdownItem, styles.dropdownNudgableUser)}
							>
								<span>{text}</span>
								{!feedback?.isAnonymous && <FontAwesomeIcon icon={faHandPointRight} />}
							</DropdownItem>
						))}
					</ArrowDropdown>
				)}
			</div>
		);
	};

	getAnswers = (questionId: string, requests: Request[]): Answer[] => {
		const answers = requests.reduce((accum: Answer[], request: Request) => {
			const allAnswers = request && request.feedbackRecord ? request.feedbackRecord.contents : [];
			const answer = allAnswers.find(({ id }: { id: string }) => id === questionId);
			accum.push({ user: request.user, value: answer && answer.value });
			return accum;
		}, []);

		return answers;
	};

	renderEmptyState = () => {
		const {
			viewer: { feedback, user },
		} = this.props;

		if (!feedback) return null;

		const { creator, requests } = feedback;
		const userIsCreator = creator && user.id === creator.id;
		const missingResponses = requests?.some(({ feedbackRecord }) => !feedbackRecord);

		return (
			<div className={cx(styles.content, stylesFeedbackRequestItem.questions, styles.emptyState)}>
				<ThemeContext.Consumer>{({ isDarkMode }) => (isDarkMode ? <IlloDark /> : <Illo />)}</ThemeContext.Consumer>
				<h3>No responses yet.</h3>
				{userIsCreator && missingResponses && (
					<>
						<h4>Would you like to choose people to remind?</h4>
						<Button size="normal" onClick={this.nudgeUsers} variant="secondary">
							Remind respondents…
						</Button>
					</>
				)}
			</div>
		);
	};

	createAnswersByQuestions(questions: QuestionType[], answeredRequests: Request[]): { [key: string]: Answer[] } {
		return questions.reduce(
			(accum: { [key: string]: Answer[] }, question) => ({
				...accum,
				[question.id]: this.getAnswers(question.id, answeredRequests),
			}),
			{},
		);
	}

	createCSVStructure(
		rawQuestions: QuestionType[],
		answeredRequests: Request[],
	): { fields: string[]; data: string[][] } {
		const questions = rawQuestions.filter(question => question.type !== "blank");

		// rows are users, columns are questions.
		// column, headers.
		const columns = questions.reduce(
			(acc: Map<string, { title: string; index: number }>, question, index: number) => {
				acc.set(question.id, { title: question.title, index: index + 2 });
				return acc;
			},
			new Map([
				["user", { title: "Name", index: 0 }],
				["email", { title: "Email", index: 1 }],
			]),
		);

		const users: { [key: string]: string[] } = {};

		for (let question of questions) {
			const column = columns.get(question.id);

			if (!column) {
				toast.error("Something went wrong, export will not be complete.");
				Sentry.captureMessage("Unreachable case reached, column for question was null");
				continue;
			}

			for (let answer of this.getAnswers(question.id, answeredRequests)) {
				if (!users[answer.user.id]) {
					users[answer.user.id] = [
						answer.user.fullName ? answer.user.fullName : "",
						answer.user.email ? answer.user.email : "",
					];
				}

				users[answer.user.id][column.index] = questionAnswerToText(question, answer);
			}
		}

		const rows = Object.values(users);

		return { fields: Array.from(columns.values()).map(column => column.title), data: rows };
	}

	createCSVData = () => {
		const { viewer } = this.props;
		const { feedback } = viewer;

		if (!feedback) return null;

		const questions = feedback.info?.questions ?? [];

		if (questions.length === 0) {
			return null;
		}

		try {
			if (!validateQuestions(questions)) {
				/* Questions seem to be invalid */
				toast.error("Something went wrong");
				Sentry.captureMessage("InvalidQuestions: CSV export feedback question validation failed.");
				return null;
			}
		} catch (error) {
			toast.error("Something went wrong");
			Sentry.captureException(error);
			return null;
		}

		const answeredRequests = feedback.requests
			? feedback.requests.filter(request => request && request.feedbackRecord)
			: [];

		let filteredRequests = answeredRequests;
		if (this.state.requestee !== USER_FILTER_DEFAULT_VALUE) {
			filteredRequests = answeredRequests.filter(request => request.user.id === this.state.requestee);
		}

		const csvStructure = this.createCSVStructure(questions, filteredRequests);

		const { threeSixtyRelation } = feedback;

		const threeSixtyRelationName = threeSixtyRelation
			? ThreeSixtyRelationPluralReverse[threeSixtyRelation.toString() as keyof typeof ThreeSixtyRelationPluralReverse]
			: undefined;

		const filename = `${threeSixtyRelationName || "feedback_export"}.csv`;

		// \uFEFF is the utf8 byte order marker (BOM), it clues exel into the fact that the file is utf8
		// emojis will not work without this (in exel).
		return { filename: filename, content: `\uFEFF${Papa.unparse(csvStructure)}` };
	};

	onExportCSV = async () => {
		const {
			viewer: { feedback },
		} = this.props;

		if (!feedback || !feedback.id) return null;

		const { isThreeSixtyParent } = feedback;

		if (!isThreeSixtyParent) {
			const csv = this.createCSVData();

			if (csv == null) {
				toast.error("Unable to create CSV");
				Sentry.captureMessage("Unable to create CSV");
				return null;
			}

			const blob = new Blob([csv.content], { type: "text/csv" });
			download(csv.filename, blob);
		} else {
			const csvs = await Promise.all(this.childCSVExportMethods).then(csvMethods => csvMethods.map(method => method()));

			const zip = new JSZip();

			for (let csv of csvs) {
				if (csv == null) {
					continue;
				}
				zip.file(csv.filename, csv.content);
			}

			zip.generateAsync({ type: "blob" }).then(completedZip => {
				download("feedback_export.zip", completedZip);
			});
		}

		return null;
	};

	renderContent = () => {
		const { viewer } = this.props;
		const { feedback } = viewer;

		if (!feedback) return null;

		const { type, recordList, creator } = feedback;

		if (type === "UNSOLICITED") {
			if (!recordList || !creator) return null;
			return (
				<NoteAndFeedbackHistoryPaywall
					viewer={viewer}
					feature="feedback"
					text="unsolicited feedback"
					creationDate={moment(feedback.createdAt || "")}
				>
					<div className={styles.unsolicitedContainer}>
						{renderUser(creator)}
						<p>{replaceLinks(recordList[0].contents[0].value)}</p>
					</div>
				</NoteAndFeedbackHistoryPaywall>
			);
		}

		const { tab } = this.state;
		const questions = feedback.info ? feedback.info.questions : [];

		if (!validateQuestions(questions)) {
			/* Questions seem to be invalid */
			return;
		}

		const answeredRequests = feedback.requests
			? feedback.requests.filter(request => request && request.feedbackRecord)
			: [];

		if (answeredRequests.length === 0) return this.renderEmptyState();

		let filteredRequests = answeredRequests;
		if (this.state.requestee !== USER_FILTER_DEFAULT_VALUE) {
			filteredRequests = answeredRequests.filter(request => request.user.id === this.state.requestee);
		}

		const answersByQuestion = this.createAnswersByQuestions(questions, answeredRequests);

		return (
			<NoteAndFeedbackHistoryPaywall
				viewer={viewer}
				feature="feedback"
				text="feedback that was requested"
				creationDate={moment(feedback.createdAt || "")}
			>
				<div className={cx(styles.content, stylesFeedbackRequestItem.questions)}>
					{this.renderTabs()}
					{tab === "By Question" &&
						questions.map(question => (
							<Question
								key={question.id}
								question={question}
								answers={answersByQuestion[question.id]}
								isAnonymous={feedback.isAnonymous as boolean}
								currentUser={viewer.user}
								feedbackId={feedback.id}
							/>
						))}
					{tab === "By Person" &&
						filteredRequests.map(request => (
							<Person key={request.id} viewer={viewer} questions={questions} instance={request} feedback={feedback} />
						))}
				</div>
			</NoteAndFeedbackHistoryPaywall>
		);
	};

	registerCSVExportMethod = (method: Promise<CSVExportMethodType>) => {
		this.childCSVExportMethods.push(method);
	};

	renderContentWrapper(feedback: NonNullable<Props["viewer"]["feedback"]>, user: { id: string; fullName: string }) {
		const { isThreeSixtyParent, children, threeSixtyRelation } = feedback;

		if (isThreeSixtyParent) {
			if (!children) return null;

			const threeSixtyTabs = calculateTabs(children);

			const selectedTab = this.getCurrentThreeSixtyTab(threeSixtyTabs);

			if (selectedTab) {
				return children.map((child: { id: string }) => {
					return (
						<FeedbackItemRenderer
							key={child.id}
							history={this.props.history}
							location={this.props.location}
							match={this.props.match}
							isChild={true}
							id={child.id}
							threeSixtyParentTab={selectedTab.key}
							registerCSVExportMethod={this.registerCSVExportMethod}
						/>
					);
				});
			} else {
				return this.renderEmptyState();
			}
		}

		const parentTab = this.props.threeSixtyParentTab;

		let relationNumber = parentTab ? ThreeSixtyRelation[parentTab] : NaN;

		const showThisFeedback = !parentTab || relationNumber === threeSixtyRelation;

		return (
			<div key={feedback.id} className={cx({ [styles.hideUnlessPrinting]: !showThisFeedback })}>
				<ListHeader className={cx(styles.customGrid)} />
				<FeedbackCard
					hideMenu
					isSpecific
					feedback={feedback}
					user={user}
					className={cx(styles.customGrid, styles.feedbackCard)}
				/>
				{this.renderContent()}
			</div>
		);
	}

	onSetThreeSixtyTab = (tab: ThreeSixtyTabKeyType) => {
		this.setState({ threeSixtyTab: tab });
	};

	render() {
		const {
			viewer: { feedback, user, account },
			location,
			isChild,
		} = this.props;

		const { hasAccessToFeedback } = account.billingAccount;
		const { state } = location;
		const { nudgeOpen, reuseQuestionsOpen } = this.state;

		if (!feedback || !feedback.id) return null;

		const { children } = feedback;

		const modals = (id: string) => {
			return (
				<>
					{nudgeOpen && <NudgeRequestees id={id} onClose={this.onNudgeClose} selected={this.state.nudgeSelected} />}
					{reuseQuestionsOpen && (
						<ReuseFeedbackQuestions
							id={id}
							open
							onClose={this.onReuseQuestionsClose}
							onComplete={this.onReuseQuestionsComplete}
						/>
					)}
				</>
			);
		};

		if (isChild) {
			return (
				<>
					{this.renderContentWrapper(feedback, user)}
					{modals(feedback.id)}
				</>
			);
		}

		const { isThreeSixtyParent } = feedback;

		const threeSixtyTabs = calculateTabs(children);

		const threeSixtyTab = this.getCurrentThreeSixtyTab(threeSixtyTabs)?.key;

		return (
			<div className={stylesFeedbackRequestItem.body}>
				{this.renderHeader()}
				<div className={styles.listHeader} />
				{isThreeSixtyParent && threeSixtyTab ? (
					<ComplexTabs
						tabs={threeSixtyTabs}
						tab={threeSixtyTab}
						setTab={this.onSetThreeSixtyTab}
						className={styles.threeSixtyTabs}
					/>
				) : (
					false
				)}
				{this.renderContentWrapper(feedback, user)}
				{modals(feedback.id)}
				{state && state.requestedFeedbackId && (
					<GaveFeedbackModal
						isRequest={feedback.type !== "UNSOLICITED"}
						id={state.requestedFeedbackId}
						hasAccessToFeedback={hasAccessToFeedback}
					/>
				)}
			</div>
		);
	}
}

const FeedbackItemQuery = graphql`
	query FeedbackItemQuery($id: ID!) {
		viewer {
			user {
				id
				fullName

				internalAttributes {
					manager {
						id
						fullName
						firstName
					}

					directReports {
						edges {
							node {
								id
							}
						}
					}
				}

				...VisibilityBarUser
			}

			account {
				billingAccount {
					hasAccessToFeedback: hasAccessToFeature(feature: feedback)
				}
			}

			...NoteAndFeedbackHistoryPaywall_viewer
			...BillingIdentityFeaturesPaywall_viewer
			...NotFound_viewer

			feedback: node(id: $id) {
				... on FeedbackNode {
					...FeedbackCardFeedback
					...FeedbackSubject_item
					...MoreActions_item
					...VisibilityBarFeedback

					id
					type
					info
					isDraft
					isArchived
					createdAt
					questions
					sharedWithMe
					lastSeenByUser
					isAnonymous
					isThreeSixtyParent
					threeSixtyRelation
					children {
						id
						requestCount
						responseCount
						threeSixtyRelation
					}

					creator {
						id
						email
						image
						fullName
						firstName
						lastName
						title
						department
						isGuest
					}

					recipients {
						user {
							id
						}
					}

					recordList(recordType: DIRECT) {
						contents
					}

					requests {
						id

						feedbackRecord {
							createdAt
							id
							contents
						}

						user {
							id
							email
							image
							fullName
							firstName
							lastName
							isGuest
						}

						...PersonRequest
					}
				}
			}
		}
	}
`;

const FeedbackItemRenderer = (
	outerProps: RendererProps & {
		id?: string;
	},
) => {
	const { registerCSVExportMethod: outerRegisterCSVExportMethod, ...remainingOuterProps } = outerProps;
	let { id } = outerProps;

	const outerObjectContext = useContext(OuterObjectContext);
	const teamsContext = useContext(TeamsContext);

	const [registerCSVExportMethod, setRegisterCSVExportMethod] = useState<
		{ wrappedSetRegisterCSVExportMethod: (method: CSVExportMethodType) => void } | undefined
	>(undefined);

	useEffect(() => {
		if (outerRegisterCSVExportMethod) {
			const a = new Promise<CSVExportMethodType>(resolve => {
				setRegisterCSVExportMethod({ wrappedSetRegisterCSVExportMethod: resolve });
			});

			outerRegisterCSVExportMethod(a);
		}
	}, [setRegisterCSVExportMethod, outerRegisterCSVExportMethod]);

	if (!id) {
		if (outerProps.match.params.id) {
			id = decodeURIComponent(outerProps.match.params.id);
		} else {
			return null;
		}
	}

	return (
		<QueryRenderer<FeedbackItemQueryType>
			environment={environment}
			query={FeedbackItemQuery}
			variables={{ id }}
			// eslint-disable-next-line react/jsx-no-bind
			render={({ props }) => {
				if (!props) return null;

				const { feedback } = props.viewer;
				if (!feedback) return <NotFound viewer={props.viewer} />;

				// Redirect to editor if feedback is still in draft
				if (feedback.isDraft) {
					const { history } = outerProps;
					history.push(`${history.location.pathname}draft/`);
				}

				return (
					<FeedbackItem
						outerObjectContext={outerObjectContext}
						{...props}
						{...remainingOuterProps}
						teamsContext={teamsContext}
						registerCSVExportMethod={registerCSVExportMethod}
					/>
				);
			}}
		/>
	);
};
export default FeedbackItemRenderer;
